import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  memo,
  useMemo,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./styles/_index.scss";
import store from "../../store";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";

const drawerWidth = 240;
const MS_TO_KNOTS = 1.94384;

interface ColorScale {
  threshold: number;
  color: string;
  label: string;
}

type DataType = "wind" | "wave" | "wave_period";
type WaveSubType = "wave_period";

const WIND_COLOR_SCALE: ColorScale[] = [
  { threshold: 0, color: "#f1f8e9", label: "0-10" },
  { threshold: 10, color: "#aed581", label: "10-15" },
  { threshold: 15, color: "#245FA8", label: "15-20" },
  { threshold: 20, color: "#B069DB", label: "20-25" },
  { threshold: 25, color: "#33cc99", label: "25-30" },
  { threshold: 30, color: "#ffcc00", label: "30-35" },
  { threshold: 35, color: "#ff3333", label: "35-40" },
  { threshold: 40, color: "#cc0000", label: "40-45" },
  { threshold: 45, color: "#7b0000", label: "45-50" },
  { threshold: 50, color: "#4a0018", label: "50+" },
];

const WAVE_COLOR_SCALE: ColorScale[] = [
  { threshold: 0.0, color: "#000080", label: "0-0.5" },
  { threshold: 0.5, color: "#C52569", label: "0.5-1" },
  { threshold: 1.0, color: "#3D582F", label: "1-2" },
  { threshold: 2.0, color: "#B069DB", label: "2-3" },
  { threshold: 3.0, color: "#33cc99", label: "3-4" },
  { threshold: 4.0, color: "#ffcc00", label: "4-5" },
  { threshold: 5.0, color: "#ff9933", label: "5-6" },
  { threshold: 6.0, color: "#ff3333", label: "6-7" },
  { threshold: 7.0, color: "#cc0000", label: "7-8" },
  { threshold: 8.0, color: "#7b0000", label: "8-9" },
];

const WAVE_PERIOD_COLOR_SCALE: ColorScale[] = [
  { threshold: 0,  color: "#0D47A1", label: "0-4s" },
  { threshold: 4,  color: "#00ACC1", label: "4-6s" },
  { threshold: 6,  color: "#00897B", label: "6-8s" },
  { threshold: 8,  color: "#43A047", label: "8-10s" },
  { threshold: 10, color: "#E6F216", label: "10-12s" },
  { threshold: 12, color: "#FBC02D", label: "12-14s" },
  { threshold: 14, color: "#FB8C00", label: "14-16s" },
  { threshold: 16, color: "#E53935", label: "16-18s" },
  { threshold: 18, color: "#8E24AA", label: "18-20s" },
  { threshold: 20, color: "#D81B60", label: "20s+" },
];

function buildColorScaleForVelocity(scale: ColorScale[], maxV: number, steps = 50): string[] {
  const result: string[] = [];
  for (let i = 0; i < steps; i++) {
    const speed = (i / (steps - 1)) * maxV;
    let color = scale[0].color;
    for (let k = scale.length - 1; k >= 0; k--) {
      if (speed >= scale[k].threshold) { color = scale[k].color; break; }
    }
    result.push(color);
  }
  return result;
}

function getColorScale(t: DataType): ColorScale[] {
  if (t === "wind") return WIND_COLOR_SCALE;
  if (t === "wave_period") return WAVE_PERIOD_COLOR_SCALE;
  return WAVE_COLOR_SCALE;
}

function getLegendLabel(t: DataType): string {
  if (t === "wind") return "Wind Speed (knots)";
  if (t === "wave_period") return "Wave Period (s)";
  return "Wave Height (m)";
}

function getVelocityConfig(t: DataType) {
  if (t === "wind")
    return {
      colorScale: buildColorScaleForVelocity(WIND_COLOR_SCALE, 60),
      maxVelocity: 65,
      velocityScale: 0.003,
      particleAge: 60,
      lineWidth: 2.5,
      particleMultiplier: 1 / 280,
      minVelocity: 0,
      opacity: 0.97,
      speedUnit: "kt",
    };
  return {
    colorScale: buildColorScaleForVelocity(WAVE_COLOR_SCALE, 8),
    maxVelocity: 8,
    velocityScale: 0.0080,
    particleAge: 20,
    lineWidth: 4.5,
    particleMultiplier: 1 / 180,
    minVelocity: 0.1,
    opacity: 1.0,
    speedUnit: "m",
  };
}

const isWaveMode = (t: DataType) => t !== "wind";

function arrayMin(arr: number[]): number {
  let m = Infinity;
  for (let i = 0; i < arr.length; i++) if (arr[i] < m) m = arr[i];
  return m;
}

function arrayMax(arr: number[]): number {
  let m = -Infinity;
  for (let i = 0; i < arr.length; i++) if (arr[i] > m) m = arr[i];
  return m;
}

let _velocityScriptPromise: Promise<boolean> | null = null;

function pointInPolygon(lat: number, lon: number, polygon: number[][]): boolean {
  let inside = false;
  const x = lon, y = lat;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

interface LandRing {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
  ring: number[][];
}

const _landRingCache = new WeakMap<any, LandRing[]>();

function getLandRings(landGeoJSON: any): LandRing[] {
  const cached = _landRingCache.get(landGeoJSON);
  if (cached) return cached;

  const rings: LandRing[] = [];
  for (const feature of landGeoJSON.features || []) {
    const geom = feature.geometry;
    if (!geom) continue;
    const polys =
      geom.type === "Polygon"
        ? [geom.coordinates]
        : geom.type === "MultiPolygon"
        ? geom.coordinates
        : [];
    for (const poly of polys) {
      const ring = poly[0];
      if (!ring?.length) continue;
      let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
      for (const pt of ring) {
        const lon = pt[0], lat = pt[1];
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
      }
      rings.push({ minLat, maxLat, minLon, maxLon, ring });
    }
  }
  _landRingCache.set(landGeoJSON, rings);
  return rings;
}

function isOverLandWithBuffer(
  lat: number,
  lon: number,
  landGeoJSON: any,
  buffer: number = 0
): boolean {
  if (!landGeoJSON?.features) return false;
  const rings = getLandRings(landGeoJSON);

  const qMinLat = lat - buffer, qMaxLat = lat + buffer;
  const qMinLon = lon - buffer, qMaxLon = lon + buffer;

  const testPoints =
    buffer > 0
      ? [
          [lat, lon],
          [lat + buffer, lon],
          [lat - buffer, lon],
          [lat, lon + buffer],
          [lat, lon - buffer],
        ]
      : [[lat, lon]];

  for (const r of rings) {
    if (qMaxLat < r.minLat || qMinLat > r.maxLat || qMaxLon < r.minLon || qMinLon > r.maxLon) continue;
    for (const [tlat, tlon] of testPoints) {
      if (pointInPolygon(tlat, tlon, r.ring)) return true;
    }
  }
  return false;
}

let _landGeoJSONPromise: Promise<any> | null = null;
let _boundariesGeoJSONPromise: Promise<any> | null = null;

function ensureVelocityScript(): Promise<boolean> {
  if (_velocityScriptPromise) return _velocityScriptPromise;
  _velocityScriptPromise = new Promise<boolean>((resolve) => {
    if ((window as any).L?.velocityLayer) { resolve(true); return; }
    const tryLoad = (src: string, fb?: string) => {
      const s = document.createElement("script");
      s.src = src; s.async = true; s.crossOrigin = "anonymous";
      s.onload = () => resolve(true);
      s.onerror = () => (fb ? tryLoad(fb) : resolve(false));
      document.head.appendChild(s);
    };
    tryLoad(
      "https://cdn.jsdelivr.net/npm/leaflet-velocity@1.9.0/dist/leaflet-velocity.min.js",
      "https://unpkg.com/leaflet-velocity@1.9.0/dist/leaflet-velocity.min.js"
    );
  });
  return _velocityScriptPromise;
}
ensureVelocityScript();

async function loadLandGeoJSON() {
  if (_landGeoJSONPromise) return _landGeoJSONPromise;
  _landGeoJSONPromise = (async () => {
    try {
      const r = await fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_land.geojson");
      if (r.ok) return await r.json();
    } catch {}
    return null;
  })();
  return _landGeoJSONPromise;
}

async function loadBoundariesGeoJSON() {
  if (_boundariesGeoJSONPromise) return _boundariesGeoJSONPromise;
  _boundariesGeoJSONPromise = (async () => {
    try {
      const r = await fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_boundary_lines_land.geojson");
      if (r.ok) return await r.json();
    } catch {}
    return null;
  })();
  return _boundariesGeoJSONPromise;
}

loadLandGeoJSON();
loadBoundariesGeoJSON();

interface WindWaveData {
  time: string;
  lats: number[];
  lons: number[];
  u: number[][];
  v: number[][];
  speed: number[][];
  direction?: number[][];
  wvhgt?: (number | null)[][];
  wvdir?: (number | null)[][];
  wvper?: (number | null)[][];
  fetchedAt?: number;
}

function validateData(d: WindWaveData) {
  if (!d.lats?.length || !d.lons?.length) return false;
  const hasUV =
    d.u?.length &&
    d.v?.length &&
    d.u.length === d.lats.length &&
    d.u[0]?.length === d.lons.length;
  const hasWave =
    (d.wvhgt?.length && d.wvdir?.length) ||
    d.wvper?.length;
  return !!(hasUV || hasWave);
}

const WAVE_PARTICLE_LAND_BUFFER = 0.25;

function deriveUV(
  d: WindWaveData,
  type: DataType,
  landGeoJSON: any | null = null
): { u: number[][]; v: number[][] } {
  const ny = d.lats.length, nx = d.lons.length;

  if (type === "wind") return { u: d.u, v: d.v };

  if (type === "wave") {
    const { wvhgt, wvdir, lats, lons } = d;
    if (!wvhgt?.length || !wvdir?.length) return { u: [], v: [] };

    const landMask = landGeoJSON
      ? getLandMaskForGrid(lats, lons, landGeoJSON, WAVE_PARTICLE_LAND_BUFFER)
      : null;

    const uOut: number[][] = [], vOut: number[][] = [];
    for (let i = 0; i < ny; i++) {
      const uRow: number[] = [], vRow: number[] = [];
      for (let j = 0; j < nx; j++) {
        const h = wvhgt[i]?.[j];
        const dRaw = wvdir[i]?.[j];
        const overLand = landMask ? landMask[i * nx + j] === 1 : false;
        if (overLand || h == null || dRaw == null || !Number.isFinite(h) || !Number.isFinite(dRaw)) {
          uRow.push(NaN); vRow.push(NaN); continue;
        }
        const r = ((dRaw as number) * Math.PI) / 180;
        uRow.push(-Math.sin(r) * (h as number));
        vRow.push(-Math.cos(r) * (h as number));
      }
      uOut.push(uRow); vOut.push(vRow);
    }
    return { u: uOut, v: vOut };
  }

  return { u: [], v: [] };
}

interface VelocityPayload {
  velocityData: {
    header: {
      parameterCategory: number;
      parameterNumber: number;
      dx: number; dy: number;
      nx: number; ny: number;
      la1: number; la2: number;
      lo1: number; lo2: number;
    };
    data: Float32Array;
  }[];
}

const payloadCache = new Map<string, VelocityPayload | null>();

function buildVelocityPayload(
  data: WindWaveData,
  type: DataType,
  landGeoJSON: any | null = null
): VelocityPayload | null {
  const cacheKey = `${type}-${data.fetchedAt ?? Date.now()}`;
  if (payloadCache.has(cacheKey)) return payloadCache.get(cacheKey)!;

  const { lats, lons } = data;
  if (!lats?.length || !lons?.length) return null;

  const { u, v } = deriveUV(data, type, landGeoJSON);
  if (!u?.length || !v?.length || u.length !== lats.length) return null;

  const isWind = type === "wind";
  const nx = lons.length, ny = lats.length;
  const dx = nx > 1 ? Math.abs(lons[1] - lons[0]) : 1;
  const dy = ny > 1 ? Math.abs(lats[1] - lats[0]) : 1;

  const minLat = arrayMin(lats);
  const maxLat = arrayMax(lats);
  const minLon = arrayMin(lons);
  const maxLon = arrayMax(lons);

  const uF = new Float32Array(nx * ny);
  const vF = new Float32Array(nx * ny);
  let pos = 0;

  const fillU = (val: number): number =>
    Number.isFinite(val) ? (isWind ? val * MS_TO_KNOTS : val) : NaN;
  const fillV = (val: number): number =>
    Number.isFinite(val) ? (isWind ? val * MS_TO_KNOTS : val) : NaN;

  const sortedLatIdx = lats
    .map((lat, idx) => ({ lat, idx }))
    .sort((a, b) => b.lat - a.lat)
    .map((x) => x.idx);

  for (let si = 0; si < sortedLatIdx.length; si++) {
    const li = sortedLatIdx[si];
    for (let j = 0; j < nx; j++) {
      uF[pos] = fillU(u[li]?.[j]);
      vF[pos] = fillV(v[li]?.[j]);
      pos++;
    }
  }

  const baseHdr = {
    parameterCategory: 2, parameterNumber: 2,
    dx, dy, nx, ny,
    la1: maxLat, la2: minLat,
    lo1: minLon, lo2: maxLon,
  };

  const result: VelocityPayload = {
    velocityData: [
      { header: baseHdr, data: uF },
      { header: { ...baseHdr, parameterNumber: 3 }, data: vF },
    ],
  };

  if (payloadCache.size > 20) payloadCache.delete(payloadCache.keys().next().value!);
  payloadCache.set(cacheKey, result);
  return result;
}

interface WavePeriodCanvasLayer {
  layer: L.Layer;
  remove: () => void;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const v = parseInt(h, 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

const WAVE_PERIOD_RGB_SCALE = WAVE_PERIOD_COLOR_SCALE.map((s) => ({
  threshold: s.threshold,
  rgb: hexToRgb(s.color),
}));

function getBandIndexForValue(value: number): number {
  const scale = WAVE_PERIOD_RGB_SCALE;
  for (let k = scale.length - 1; k >= 0; k--) {
    if (value >= scale[k].threshold) return k;
  }
  return 0;
}

function getDiscretePeriodColor(value: number): [number, number, number] {
  return WAVE_PERIOD_RGB_SCALE[getBandIndexForValue(value)].rgb;
}

function boxBlur2D(src: Float32Array, ny: number, nx: number, radius: number): Float32Array {
  const tmp = new Float32Array(ny * nx);
  const out = new Float32Array(ny * nx);
  const win = radius * 2 + 1;

  for (let i = 0; i < ny; i++) {
    const rowOff = i * nx;
    for (let j = 0; j < nx; j++) {
      let sum = 0;
      for (let k = -radius; k <= radius; k++) {
        const jj = j + k;
        if (jj >= 0 && jj < nx) sum += src[rowOff + jj];
      }
      tmp[rowOff + j] = sum / win;
    }
  }

  for (let j = 0; j < nx; j++) {
    for (let i = 0; i < ny; i++) {
      let sum = 0;
      for (let k = -radius; k <= radius; k++) {
        const ii = i + k;
        if (ii >= 0 && ii < ny) sum += tmp[ii * nx + j];
      }
      out[i * nx + j] = sum / win;
    }
  }

  return out;
}

function featherMask(src: Float32Array, ny: number, nx: number, radius: number, passes: number): Float32Array {
  let cur = src;
  for (let p = 0; p < passes; p++) {
    cur = boxBlur2D(cur, ny, nx, radius);
  }
  return cur;
}

const _landMaskCache = new Map<string, Uint8Array>();

function getLandMaskForGrid(
  lats: number[],
  lons: number[],
  landGeoJSON: any | null,
  buffer: number
): Uint8Array {
  const ny = lats.length, nx = lons.length;
  const key = `${ny}x${nx}|${lats[0]}|${lats[ny - 1]}|${lons[0]}|${lons[nx - 1]}|${buffer}`;
  const cached = _landMaskCache.get(key);
  if (cached) return cached;

  const mask = new Uint8Array(ny * nx);
  if (landGeoJSON) {
    for (let i = 0; i < ny; i++) {
      for (let j = 0; j < nx; j++) {
        if (isOverLandWithBuffer(lats[i], lons[j], landGeoJSON, buffer)) {
          mask[i * nx + j] = 1;
        }
      }
    }
  }

  if (_landMaskCache.size > 6) _landMaskCache.delete(_landMaskCache.keys().next().value!);
  _landMaskCache.set(key, mask);
  return mask;
}

function createWavePeriodHeatmapLayer(
  map: L.Map,
  data: WindWaveData,
  landGeoJSON: any | null
): WavePeriodCanvasLayer {
  const { lats, lons, wvper } = data;
  const ny = lats.length;
  const nx = lons.length;

  const lat0 = lats[0];
  const lon0 = lons[0];
  const dLat = ny > 1 ? lats[1] - lats[0] : 1;
  const dLon = nx > 1 ? lons[1] - lons[0] : 1;

  const COAST_BUFFER = 0.25;
  const SMOOTH_RADIUS = 2;
  const SMOOTH_PASSES = 2;
  const FILL_ALPHA = 204;
  const BORDER_ALPHA = 10;

  const landMask = getLandMaskForGrid(lats, lons, landGeoJSON, COAST_BUFFER);

  const masked = new Uint8Array(ny * nx);
  const validF = new Float32Array(ny * nx);
  const rawValueF = new Float32Array(ny * nx);

  for (let i = 0; i < ny; i++) {
    for (let j = 0; j < nx; j++) {
      const idx = i * nx + j;
      const p = wvper?.[i]?.[j];
      const isMasked = p == null || !Number.isFinite(p as number) || landMask[idx] === 1;
      masked[idx] = isMasked ? 1 : 0;
      validF[idx] = isMasked ? 0 : 1;
      rawValueF[idx] = isMasked ? 0 : (p as number);
    }
  }

  const blurredValueF = featherMask(rawValueF, ny, nx, SMOOTH_RADIUS, SMOOTH_PASSES);
  const blurredWeightF = featherMask(validF, ny, nx, SMOOTH_RADIUS, SMOOTH_PASSES);

  const smoothedPeriod = new Float32Array(ny * nx);
  for (let idx = 0; idx < ny * nx; idx++) {
    smoothedPeriod[idx] = blurredWeightF[idx] > 0.001 ? blurredValueF[idx] / blurredWeightF[idx] : NaN;
  }

  function wrapCol(j: number): number {
    return ((j % nx) + nx) % nx;
  }

  function valueAt(i: number, j: number): number {
    if (i < 0 || i >= ny) return NaN;
    const jw = wrapCol(j);
    if (masked[i * nx + jw]) return NaN;
    const v = smoothedPeriod[i * nx + jw];
    return Number.isNaN(v) ? NaN : v;
  }

  function sampleAt(lat: number, lon: number): number {
    const fi = (lat - lat0) / dLat;
    const fj = (lon - lon0) / dLon;
    const i0 = Math.floor(fi), j0 = Math.floor(fj);
    const ti = fi - i0, tj = fj - j0;

    const bTL = (1 - ti) * (1 - tj);
    const bTR = (1 - ti) * tj;
    const bBL = ti * (1 - tj);
    const bBR = ti * tj;

    const v00 = valueAt(i0, j0);
    const v01 = valueAt(i0, j0 + 1);
    const v10 = valueAt(i0 + 1, j0);
    const v11 = valueAt(i0 + 1, j0 + 1);

    let valueSum = 0;
    let weightSum = 0;
    if (!Number.isNaN(v00)) { valueSum += v00 * bTL; weightSum += bTL; }
    if (!Number.isNaN(v01)) { valueSum += v01 * bTR; weightSum += bTR; }
    if (!Number.isNaN(v10)) { valueSum += v10 * bBL; weightSum += bBL; }
    if (!Number.isNaN(v11)) { valueSum += v11 * bBR; weightSum += bBR; }

    return weightSum > 0 ? valueSum / weightSum : NaN;
  }

  const CanvasLayer = L.Layer.extend({
    _canvas: null as HTMLCanvasElement | null,
    _map: null as L.Map | null,
    _rafId: null as number | null,

    onAdd(m: L.Map) {
      this._map = m;
      const canvas = document.createElement("canvas");
      canvas.style.position = "absolute";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.pointerEvents = "none";
      canvas.style.zIndex = "400";
      this._canvas = canvas;
      m.getPanes().overlayPane!.appendChild(canvas);
      m.on("moveend zoomend viewreset resize", this._scheduleRedraw, this);
      this._redraw();
    },

    onRemove(m: L.Map) {
      m.off("moveend zoomend viewreset resize", this._scheduleRedraw, this);
      if (this._rafId != null) { cancelAnimationFrame(this._rafId); this._rafId = null; }
      if (this._canvas?.parentNode) this._canvas.parentNode.removeChild(this._canvas);
      this._canvas = null;
      this._map = null;
    },

    _scheduleRedraw() {
      if (this._rafId != null) return;
      this._rafId = requestAnimationFrame(() => { this._rafId = null; this._redraw(); });
    },

    _redraw() {
      const m: L.Map = this._map;
      const canvas: HTMLCanvasElement = this._canvas;
      if (!m || !canvas) return;

      const size = m.getSize();
      canvas.width = size.x;
      canvas.height = size.y;

      const topLeft = m.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(canvas, topLeft);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, size.x, size.y);

      const W = size.x, H = size.y;
      if (W <= 0 || H <= 0) return;

      const imgData = ctx.createImageData(W, H);
      const buf = imgData.data;
      const bandIdx = new Int16Array(W * H).fill(-1);

      const lonForCol = new Float64Array(W);
      for (let x = 0; x < W; x++) {
        lonForCol[x] = m.layerPointToLatLng(L.point(topLeft.x + x, topLeft.y)).lng;
      }
      const latForRow = new Float64Array(H);
      for (let y = 0; y < H; y++) {
        latForRow[y] = m.layerPointToLatLng(L.point(topLeft.x, topLeft.y + y)).lat;
      }

      let any = false;
      for (let y = 0; y < H; y++) {
        const lat = latForRow[y];
        const rowOff = y * W;
        for (let x = 0; x < W; x++) {
          const value = sampleAt(lat, lonForCol[x]);
          const idx = (rowOff + x) * 4;

          if (Number.isNaN(value)) {
            buf[idx + 3] = 0;
            continue;
          }
          any = true;
          const band = getBandIndexForValue(value);
          bandIdx[rowOff + x] = band;
          const [r, g, b] = WAVE_PERIOD_RGB_SCALE[band].rgb;
          buf[idx] = r; buf[idx + 1] = g; buf[idx + 2] = b;
          buf[idx + 3] = FILL_ALPHA;
        }
      }

      if (!any) return;

  
      const BORDER_R = 1, BORDER_G = 1, BORDER_B = 1;
      const BORDER_BLEND = 0;
      for (let y = 0; y < H; y++) {
        const rowOff = y * W;
        for (let x = 0; x < W; x++) {
          const band = bandIdx[rowOff + x];
          if (band < 0) continue;
          const rightBand = x + 1 < W ? bandIdx[rowOff + x + 1] : band;
          const downBand = y + 1 < H ? bandIdx[rowOff + W + x] : band;
          if ((rightBand >= 0 && rightBand !== band) || (downBand >= 0 && downBand !== band)) {
            const idx = (rowOff + x) * 4;
            buf[idx] = Math.round(buf[idx] * (1 - BORDER_BLEND) + BORDER_R * BORDER_BLEND);
            buf[idx + 1] = Math.round(buf[idx + 1] * (1 - BORDER_BLEND) + BORDER_G * BORDER_BLEND);
            buf[idx + 2] = Math.round(buf[idx + 2] * (1 - BORDER_BLEND) + BORDER_B * BORDER_BLEND);
          }
        }
      }

      ctx.putImageData(imgData, 0, 0);

      if (landGeoJSON) {
        const bounds = m.getBounds();
        const south = bounds.getSouth(), north = bounds.getNorth();
        const west = bounds.getWest(), east = bounds.getEast();
        const rings = getLandRings(landGeoJSON);
        const visibleRings = rings.filter(
          (r) => !(r.maxLat < south || r.minLat > north || r.maxLon < west || r.minLon > east)
        );

        if (visibleRings.length) {
          ctx.save();
          ctx.globalCompositeOperation = "destination-out";
          ctx.beginPath();
          for (const ringInfo of visibleRings) {
            const ring = ringInfo.ring;
            for (let i = 0; i < ring.length; i++) {
              const pt = m.latLngToContainerPoint([ring[i][1], ring[i][0]]);
              if (i === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y);
            }
            ctx.closePath();
          }
          ctx.fill();
          ctx.restore();

          ctx.globalCompositeOperation = "source-over";
          ctx.strokeStyle = "rgba(30, 30, 30, 0.75)";
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          for (const ringInfo of visibleRings) {
            const ring = ringInfo.ring;
            for (let i = 0; i < ring.length; i++) {
              const pt = m.latLngToContainerPoint([ring[i][1], ring[i][0]]);
              if (i === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y);
            }
            ctx.closePath();
          }
          ctx.stroke();
        }
      }
    },
  });

  const layerInstance = new (CanvasLayer as any)();
  layerInstance.addTo(map);

  return {
    layer: layerInstance,
    remove: () => { try { map.removeLayer(layerInstance); } catch {} },
  };
}

function getTodayUTC() {
  const n = new Date();
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}-${String(n.getUTCDate()).padStart(2, "0")}`;
}

function formatTimeForDisplay(ts: string) {
  try {
    const normalised = ts
      .trim()
      .replace(" ", "T")
      .replace(/(\.\d+)?Z?$/, "Z")
      .replace("TZ", "T00:00:00Z");
    const d = new Date(normalised);
    if (isNaN(d.getTime())) throw new Error(`Bad time: ${ts}`);
    return {
      dayName: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getUTCDay()],
      dayNumber: d.getUTCDate(),
      timeLabel: `${String(d.getUTCHours()).padStart(2, "0")} UTC`,
      fullDate: d,
      isoString: d.toISOString(),
    };
  } catch {
    return { dayName: "N/A", dayNumber: 0, timeLabel: ts, fullDate: new Date(), isoString: "" };
  }
}

interface TimeControlsProps {
  displayTimeIndex: number;
  availableTimes: string[];
  isDataLoading: boolean;
  isAnimating: boolean;
  onTimeIndexChange: (i: number) => void;
  onToggleAnimation: () => void;
}

const TimeControls = memo(
  ({ displayTimeIndex, availableTimes, isAnimating, onTimeIndexChange, onToggleAnimation }: TimeControlsProps) => {
    React.useEffect(() => {
      const s = document.createElement("style");
      s.textContent = `@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.2)}}`;
      document.head.appendChild(s);
      return () => { document.head.removeChild(s); };
    }, []);

    const days = useMemo(() => {
      const out: { dayName: string; dayNumber: number; timeIndex: number }[] = [];
      const seen = new Set<string>();
      availableTimes.forEach((t, i) => {
        const ti = formatTimeForDisplay(t);
        const k = `${ti.dayName}-${ti.dayNumber}`;
        if (!seen.has(k)) { seen.add(k); out.push({ dayName: ti.dayName, dayNumber: ti.dayNumber, timeIndex: i }); }
      });
      return out;
    }, [availableTimes]);

    const cur = availableTimes[displayTimeIndex] ? formatTimeForDisplay(availableTimes[displayTimeIndex]) : null;

    return (
      <div className="leaflet-map-time-controls">
        <div className="time-control-wrapper">
          <button onClick={onToggleAnimation} className={`play-button ${isAnimating ? "pause" : "play"}`} disabled={!availableTimes.length}>
            {isAnimating ? "\u23F8\uFE0F" : "\u25B6\uFE0F"}
          </button>
          {cur && (
            <div className="current-time-display">
              <div className="time-badge" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isAnimating && (
                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#4CAF50", animation: "pulse 1.5s ease-in-out infinite" }} />
                )}
                <span>{cur.dayName} {cur.dayNumber} - {cur.timeLabel}</span>
              </div>
            </div>
          )}
          <div className="day-selector">
            {days.map((day, idx) => (
              <button
                key={`${day.dayName}-${day.dayNumber}`}
                className={`day-button ${displayTimeIndex >= day.timeIndex && (idx === days.length - 1 || displayTimeIndex < days[idx + 1].timeIndex) ? "active" : ""}`}
                onClick={() => onTimeIndexChange(day.timeIndex)}
                disabled={!availableTimes.length}
              >
                <span className="day-name">{day.dayName}</span>
                <span className="day-number">{day.dayNumber}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

const WAVE_SUB_TYPES: { id: WaveSubType; label: string }[] = [
  { id: "wave_period", label: "Wave Period" },
];

function initAllPanePositions(map: L.Map) {
  try {
    const panes = map.getPanes();
    if (!panes) return;
    map.getContainer().querySelectorAll(".leaflet-pane").forEach((el) => {
      if (el instanceof HTMLElement && !("_leaflet_pos" in el)) {
        try { L.DomUtil.setPosition(el, L.point(0, 0)); } catch {}
      }
    });
    Object.values(panes).forEach((pane) => {
      if (pane instanceof HTMLElement && !("_leaflet_pos" in pane)) {
        try { L.DomUtil.setPosition(pane, L.point(0, 0)); } catch {}
      }
    });
  } catch {}
}

(function patchLeafletGetPosition() {
  if ((L.DomUtil as any)._getPositionPatched) return;
  const orig = (L.DomUtil as any).getPosition;
  (L.DomUtil as any).getPosition = function (el: HTMLElement) {
    if (!el || !("_leaflet_pos" in el)) return L.point(0, 0);
    return orig.call(this, el);
  };
  (L.DomUtil as any)._getPositionPatched = true;

  const mapProto = (L.Map as any).prototype;
  if (!mapProto._getMapPanePosPatched) {
    const origGetMapPanePos = mapProto._getMapPanePos;
    mapProto._getMapPanePos = function () {
      if (!this._mapPane) return L.point(0, 0);
      try {
        return origGetMapPanePos.call(this);
      } catch {
        return L.point(0, 0);
      }
    };
    mapProto._getMapPanePosPatched = true;
  }
})();

interface LeafletMapProps {
  height?: string;
  defaultLocation?: { longitude: number; latitude: number; zoom: number };
}

const LeafletMap: React.FC<LeafletMapProps> = ({
  height = "100%",
  defaultLocation = { longitude: 0, latitude: 0, zoom: 2 },
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const windowWidth = useRef(window.innerWidth);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [open, setOpen] = useState(windowWidth.current > 1000);
  const reduxData = useSelector((state: any) => state?.app);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage] = useState("Initializing map...");

  const [topLevel, setTopLevel] = useState<"wind" | "wave">("wind");
  const [dataType, setDataType] = useState<DataType>("wind");
  const dataTypeRef = useRef<DataType>("wind");
  const [activeSubType, setActiveSubType] = useState<WaveSubType | null>(null);

  const timeIndexRef = useRef(0);
  const [displayTimeIndex, setDisplayTimeIndex] = useState(0);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const availableTimesRef = useRef<string[]>([]);

  const isAnimatingRef = useRef(false);
  const [isAnimating, _setIsAnimating] = useState(false);
  const animationChainRef = useRef<NodeJS.Timeout | null>(null);
  const setIsAnimating = useCallback((val: boolean) => {
    isAnimatingRef.current = val;
    _setIsAnimating(val);
  }, []);

  const [currentData, setCurrentData] = useState<WindWaveData | null>(null);
  const currentDataRef = useRef<WindWaveData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isSwitchingData, setIsSwitchingData] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const dataCache = useRef<Map<string, WindWaveData>>(new Map());
  const lastFetchedKey = useRef("");
  const isInitialized = useRef(false);
  const isSwitchingDataType = useRef(false);
  const activeFetchController = useRef<AbortController | null>(null);

  const velocityLayerRef = useRef<any>(null);
  const wavePeriodLayerRef = useRef<WavePeriodCanvasLayer | null>(null);

  const landGeoJSONRef = useRef<any>(null);
  const boundariesGeoJSONRef = useRef<any>(null);
  const landOverlayLayerRef = useRef<L.TileLayer | L.GeoJSON | L.LayerGroup | null>(null);
  const labelsLayerRef = useRef<L.TileLayer | null>(null);
  const osmTileLayerRef = useRef<L.TileLayer | null>(null);
  const noLabelsTileLayerRef = useRef<L.TileLayer | null>(null);
  const baseTileLayerRef = useRef<L.TileLayer | null>(null);
  const landOverlayCacheRef = useRef<{ group: L.LayerGroup; labelsLayer: L.TileLayer } | null>(null);

  const [isGeoJSONReady, setIsGeoJSONReady] = useState(false);
  const isParticleSystemReady = useRef(false);
  const boundsSetOnce = useRef(false);
  const isUpdatingData = useRef(false);
  const isMapReady = useRef(false);
  const lastDataBoundsRef = useRef<L.LatLngBounds | null>(null);

  const memoizedDefaultLocation = useMemo(
    () => defaultLocation,
    [defaultLocation.longitude, defaultLocation.latitude, defaultLocation.zoom]
  );

  const [mapLeftOffset, setMapLeftOffset] = useState(
    window.innerWidth >= 1960 && window.innerWidth < 2200 ? 22 :
    window.innerWidth >= 2200 && window.innerWidth < 2400 ? -20 : 0
  );

  useEffect(() => { dataTypeRef.current = dataType; }, [dataType]);
  useEffect(() => { availableTimesRef.current = availableTimes; }, [availableTimes]);
  useEffect(() => { currentDataRef.current = currentData; }, [currentData]);

  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth <= 768);
      setMapLeftOffset(
        window.innerWidth >= 1960 && window.innerWidth < 2200 ? 22 :
        window.innerWidth >= 2200 && window.innerWidth < 2400 ? -20 : 0
      );
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    Promise.all([loadLandGeoJSON(), loadBoundariesGeoJSON()]).then(([land, bounds]) => {
      landGeoJSONRef.current = land;
      boundariesGeoJSONRef.current = bounds;
      setIsGeoJSONReady(true);
    });
  }, []);

  const isMapValid = useCallback(() => {
    if (!mapRef.current) return false;
    try {
      const c = mapRef.current.getContainer();
      if (!c || !c.offsetParent || !document.body.contains(c)) return false;
      return !!mapRef.current.getPanes()?.mapPane;
    } catch { return false; }
  }, []);

  const ensureMapPanePosition = useCallback(() => {
    if (!mapRef.current) return;
    try {
      const map = mapRef.current;
      const mapPane = map.getPane("mapPane");
      if (mapPane instanceof HTMLElement && !("_leaflet_pos" in mapPane)) {
        L.DomUtil.setPosition(mapPane, L.point(0, 0));
      }
      const panes = map.getPanes();
      if (panes) {
        Object.values(panes).forEach((pane) => {
          if (pane instanceof HTMLElement && !("_leaflet_pos" in pane)) {
            try { L.DomUtil.setPosition(pane, L.point(0, 0)); } catch {}
          }
        });
      }
      map.getContainer().querySelectorAll(".leaflet-pane").forEach((el) => {
        if (el instanceof HTMLElement && !("_leaflet_pos" in el)) {
          try { L.DomUtil.setPosition(el, L.point(0, 0)); } catch {}
        }
      });
    } catch {}
  }, []);

  const cleanupWavePeriodLayer = useCallback(() => {
    if (wavePeriodLayerRef.current) {
      wavePeriodLayerRef.current.remove();
      wavePeriodLayerRef.current = null;
    }
  }, []);

  const updateLandOverlay = useCallback(async () => {
    if (!isMapValid()) return;
    const map = mapRef.current!;

    if (landOverlayCacheRef.current?.group && map.hasLayer(landOverlayCacheRef.current.group))
      map.removeLayer(landOverlayCacheRef.current.group);
    if (landOverlayCacheRef.current?.labelsLayer && map.hasLayer(landOverlayCacheRef.current.labelsLayer))
      map.removeLayer(landOverlayCacheRef.current.labelsLayer);
    if (noLabelsTileLayerRef.current && map.hasLayer(noLabelsTileLayerRef.current))
      map.removeLayer(noLabelsTileLayerRef.current);
    landOverlayLayerRef.current = null;
    labelsLayerRef.current = null;

    if (osmTileLayerRef.current && !map.hasLayer(osmTileLayerRef.current)) {
      osmTileLayerRef.current.addTo(map);
      baseTileLayerRef.current = osmTileLayerRef.current;
    }
  }, [isMapValid]);

  const getMinZoom = (width: number): number => {
    if (width >= 2900) return 3.39;
    if (width >= 2200) return 3.39;
    if (width >= 2000) return 3.39;
    if (width >= 1960) return 3.0;
    if (width >= 1900) return 3.4;
    if (width >= 1800) return 2.23;
    if (width >= 1440) return 2.2;
    if (width >= 1300) return 2.5;
    if (width >= 1024) return 2.25;
    return 2.25;
  };

  const getLngOffset = (width: number): number => {
    if (width >= 1900 && width < 2000) return 40;
    return 0;
  };

  const updateMapBounds = useCallback(
    (d: WindWaveData) => {
      if (!isMapValid()) return;
      const map = mapRef.current!;
      const minLat = Math.max(-85.051129, arrayMin(d.lats));
      const maxLat = Math.min(85.051129, arrayMax(d.lats));
      const minLon = Math.max(-180, arrayMin(d.lons));
      const maxLon = Math.min(180, arrayMax(d.lons));
      const bounds = L.latLngBounds([minLat, minLon], [maxLat, maxLon]);
      const minZoom = getMinZoom(window.innerWidth);
      map.setMaxBounds(bounds);
      map.setMinZoom(minZoom);
      (map as any).getMinZoom = () => minZoom;
      map.setMaxZoom(isWaveMode(dataTypeRef.current) ? 5 : 8);
      map.fitBounds(bounds, { padding: [10, 10], maxZoom: minZoom, animate: false });
      const lngOffset = getLngOffset(window.innerWidth);
      if (lngOffset !== 0) {
        const currentCenter = map.getCenter();
        map.setView([currentCenter.lat, currentCenter.lng + lngOffset], map.getZoom(), { animate: false });
      }
      boundsSetOnce.current = true;
    },
    [isMapValid]
  );

  const applyWavePeriodLayer = useCallback(
    async (d: WindWaveData, isFirstTime: boolean) => {
      if (!isMapValid() || !isMapReady.current) {
        setTimeout(() => applyWavePeriodLayer(d, isFirstTime), 200);
        return;
      }
      const map = mapRef.current!;

      if (isFirstTime || !boundsSetOnce.current) updateMapBounds(d);

      await updateLandOverlay();

      cleanupWavePeriodLayer();

      try {
        wavePeriodLayerRef.current = createWavePeriodHeatmapLayer(map, d, landGeoJSONRef.current);
        isParticleSystemReady.current = true;
        setCurrentData(d);
      } catch (e) {
        console.error("[LeafletMap] Failed to create wave period heatmap layer:", e);
        setError("Failed to render wave period heatmap");
        isParticleSystemReady.current = false;
      }
    },
    [updateMapBounds, updateLandOverlay, cleanupWavePeriodLayer, isMapValid]
  );

  const applyVelocityLayer = useCallback(
    async (d: WindWaveData, isFirstTime: boolean) => {
      if (!isMapValid() || !isMapReady.current) {
        setTimeout(() => applyVelocityLayer(d, isFirstTime), 200);
        return;
      }

      const map = mapRef.current!;
      const type = dataTypeRef.current;

      if (type === "wave_period") {
        if (velocityLayerRef.current) {
          try {
            if (map.hasLayer(velocityLayerRef.current)) map.removeLayer(velocityLayerRef.current);
          } catch {}
          velocityLayerRef.current = null;
        }
        await applyWavePeriodLayer(d, isFirstTime);
        return;
      }

      cleanupWavePeriodLayer();

      const cfg = getVelocityConfig(type);
      const payload = buildVelocityPayload(
        d,
        type,
        isWaveMode(type) ? landGeoJSONRef.current : null
      );
      if (!payload) return;

      if (isFirstTime || !boundsSetOnce.current) updateMapBounds(d);

      const ok = await ensureVelocityScript();
      if (!ok || !(window as any).L?.velocityLayer) {
        setError("Failed to load particle animation library.");
        return;
      }

      const layerType = velocityLayerRef.current?._currentDataType;
      const canSmooth =
        !isFirstTime &&
        isParticleSystemReady.current &&
        velocityLayerRef.current &&
        layerType === type;

      if (canSmooth) {
        try {
          if (typeof velocityLayerRef.current.setData === "function") {
            velocityLayerRef.current.setData(payload.velocityData);
            setCurrentData(d);
            return;
          }
          if (velocityLayerRef.current._params) {
            velocityLayerRef.current._params.data = payload.velocityData;
            if (velocityLayerRef.current._windy)
              velocityLayerRef.current._windy.setData(payload.velocityData);
            if (typeof velocityLayerRef.current._drawWindy === "function")
              velocityLayerRef.current._drawWindy();
            else if (typeof velocityLayerRef.current.redraw === "function")
              velocityLayerRef.current.redraw();
            setCurrentData(d);
            return;
          }
        } catch {}
      }

      if (velocityLayerRef.current) {
        try {
          if (map.hasLayer(velocityLayerRef.current)) map.removeLayer(velocityLayerRef.current);
        } catch {}
        velocityLayerRef.current = null;
        isParticleSystemReady.current = false;
      }

      await updateLandOverlay();

      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (!isMapValid()) return;

      try { map.invalidateSize({ animate: false }); } catch {}
      ensureMapPanePosition();

      try {
        const layer = (window as any).L.velocityLayer({
          displayValues: false,
          displayOptions: {
            velocityType: isWaveMode(type) ? "Wave" : "Wind",
            position: "bottomleft",
            emptyString: isWaveMode(type) ? "No wave data" : "No wind data",
            speedUnit: cfg.speedUnit,
          },
          data: payload.velocityData,
          maxVelocity: cfg.maxVelocity,
          velocityScale: cfg.velocityScale,
          colorScale: cfg.colorScale,
          particleAge: cfg.particleAge,
          lineWidth: cfg.lineWidth,
          particleMultiplier: cfg.particleMultiplier,
          minVelocity: cfg.minVelocity,
          opacity: cfg.opacity,
          frameRate: isWaveMode(type) ? 15 : 25,
          useLayerBounds: true,
        });

        if (typeof layer._onLayerDidMove === "function") {
          const origMove = layer._onLayerDidMove.bind(layer);
          layer._onLayerDidMove = () => {
            try { ensureMapPanePosition(); origMove(); } catch (e) { console.warn("[LeafletMap] _onLayerDidMove suppressed:", e); }
          };
        }

        if (typeof layer.drawLayer === "function") {
          const origDraw = layer.drawLayer.bind(layer);
          layer.drawLayer = (...args: any[]) => {
            try { ensureMapPanePosition(); origDraw(...args); } catch (e) { console.warn("[LeafletMap] drawLayer suppressed:", e); }
          };
        }

        velocityLayerRef.current = layer;
        layer.addTo(map);
        ensureMapPanePosition();
        layer._currentDataType = type;
        isParticleSystemReady.current = true;
        setCurrentData(d);
      } catch (e) {
        console.error("[LeafletMap] Failed to create velocity layer:", e);
        setError("Failed to create particle animation layer");
        velocityLayerRef.current = null;
        isParticleSystemReady.current = false;
      }
    },
    [
      updateMapBounds,
      updateLandOverlay,
      applyWavePeriodLayer,
      cleanupWavePeriodLayer,
      isMapValid,
      ensureMapPanePosition,
    ]
  );

  const cleanupParticleSystem = useCallback(() => {
    isParticleSystemReady.current = false;
    if (velocityLayerRef.current && mapRef.current) {
      try {
        if (mapRef.current.hasLayer(velocityLayerRef.current)) mapRef.current.removeLayer(velocityLayerRef.current);
      } catch {}
      velocityLayerRef.current = null;
    }
    cleanupWavePeriodLayer();
    boundsSetOnce.current = false;
    isUpdatingData.current = false;
    lastDataBoundsRef.current = null;
  }, [cleanupWavePeriodLayer]);

  const fetchAndRender = useCallback(
    async (forceFetch = false): Promise<WindWaveData | null> => {
      if (!mapRef.current) return null;
      const type = dataTypeRef.current;
      const idx = timeIndexRef.current;
      const cacheKey = `${type}-${idx}`;
      const isFirstTime = !isParticleSystemReady.current;

      if (!forceFetch && lastFetchedKey.current === cacheKey && dataCache.current.has(cacheKey)) {
        const cached = dataCache.current.get(cacheKey)!;
        await applyVelocityLayer(cached, isFirstTime);
        return cached;
      }

      activeFetchController.current?.abort();
      const ctrl = new AbortController();
      activeFetchController.current = ctrl;

      if (!isAnimatingRef.current || forceFetch) setIsDataLoading(true);
      setError(null);

      try {
        const wave = isWaveMode(type);
        const ep = wave ? "/api/wave/flow_field/" : "/api/wind/flow_field/";
        const base = process.env.REACT_APP_BACKEND_IP || "";
        let url = `${base}${ep}?time_index=${idx}`;
        if (wave) url += `&wave_type=${type}`;

        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status} - ${await res.text()}`);

        const d: WindWaveData = await res.json();
        if (!validateData(d)) throw new Error("Invalid data structure");

        const tagged = { ...d, dataType: type, fetchedAt: Date.now() };
        if (dataCache.current.size > 15) dataCache.current.delete(dataCache.current.keys().next().value!);
        dataCache.current.set(cacheKey, tagged);
        lastFetchedKey.current = cacheKey;

        await applyVelocityLayer(tagged, isFirstTime);
        return tagged;
      } catch (e: any) {
        if (e.name === "AbortError") return null;
        setError(`Failed to load ${type} data: ${e.message}`);
        setCurrentData(null);
        return null;
      } finally {
        setIsDataLoading(false);
      }
    },
    [applyVelocityLayer]
  );

  const fetchAllTimestamps = useCallback(async (): Promise<string[]> => {
    const type = dataTypeRef.current;
    const wave = isWaveMode(type);
    const ep = wave ? "/api/wave/flow_field/" : "/api/wind/flow_field/";
    const base = process.env.REACT_APP_BACKEND_IP || "";
    const today = getTodayUTC();

    const fut = (ts: string[]) => {
      const f = ts.filter((t) => t.split("T")[0] >= today);
      return f.length ? f : ts;
    };

    const waveTypeParam = wave ? `?wave_type=${type}` : "";

    try {
      const r = await fetch(`${base}${ep}all_timestamps/${waveTypeParam}`, { signal: AbortSignal.timeout(15000) });
      if (r.ok) {
        const j = await r.json();
        if (j.times?.length) { const t = fut(j.times); setAvailableTimes(t); return t; }
      }
    } catch {}

    try {
      const r = await fetch(`${base}${ep}metadata/${waveTypeParam}`, { signal: AbortSignal.timeout(15000) });
      if (r.ok) {
        const m = await r.json();
        if (m.times?.length) { const t = fut(m.times); setAvailableTimes(t); return t; }
        if (m.time_steps && m.start_time && m.time_interval_hours) {
          const start = new Date(m.start_time).getTime();
          const times = Array.from({ length: m.time_steps }, (_, i) =>
            new Date(start + i * m.time_interval_hours * 3600000).toISOString()
          );
          const t = fut(times);
          setAvailableTimes(t);
          return t;
        }
      }
    } catch {}

    setAvailableTimes([]);
    setError("Failed to load timestamps");
    return [];
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    setIsLoading(true);

    const map = L.map(mapContainerRef.current, {
      center: [memoizedDefaultLocation.latitude, memoizedDefaultLocation.longitude + 40],
      zoom: memoizedDefaultLocation.zoom,
      zoomControl: false,
      attributionControl: true,
      preferCanvas: true,
      maxZoom: 8,
      minZoom: 2,
    });
    mapRef.current = map;

    try {
      const mapPane = map.getPane("mapPane");
      if (mapPane instanceof HTMLElement) L.DomUtil.setPosition(mapPane, L.point(0, 0));
    } catch {}

    const originalGetPane = map.getPane.bind(map);
    (map as any).getPane = function (name: string | HTMLElement) {
      const pane = originalGetPane(name);
      if (pane instanceof HTMLElement && !("_leaflet_pos" in pane)) {
        try { L.DomUtil.setPosition(pane, L.point(0, 0)); } catch {}
      }
      return pane;
    };

    map.on("layeradd zoomanim moveend zoomend viewreset", () => {
      if (isMapReady.current) { try { initAllPanePositions(map); } catch {} }
    });

    osmTileLayerRef.current = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors", maxZoom: 19, className: "base-ocean-tiles",
    });

    osmTileLayerRef.current.addTo(map);
    baseTileLayerRef.current = osmTileLayerRef.current;

    map.whenReady(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try { initAllPanePositions(map); } catch {}
          setTimeout(() => {
            if (!mapRef.current) return;
            try { mapRef.current.invalidateSize({ animate: false }); initAllPanePositions(mapRef.current); } catch {}
            isMapReady.current = true;
            setIsLoading(false);
          }, 300);
        });
      });
    });

    return () => {
      isMapReady.current = false;
      if (animationChainRef.current) { clearTimeout(animationChainRef.current); animationChainRef.current = null; }
      isParticleSystemReady.current = false;
      if (velocityLayerRef.current && mapRef.current) {
        try { mapRef.current.removeLayer(velocityLayerRef.current); } catch {}
        velocityLayerRef.current = null;
      }
      if (wavePeriodLayerRef.current) {
        wavePeriodLayerRef.current.remove();
        wavePeriodLayerRef.current = null;
      }
      if (landOverlayCacheRef.current && mapRef.current) {
        try {
          if (mapRef.current.hasLayer(landOverlayCacheRef.current.group)) mapRef.current.removeLayer(landOverlayCacheRef.current.group);
          if (mapRef.current.hasLayer(landOverlayCacheRef.current.labelsLayer)) mapRef.current.removeLayer(landOverlayCacheRef.current.labelsLayer);
        } catch {}
        landOverlayCacheRef.current = null;
      }
      for (const ref of [landOverlayLayerRef, labelsLayerRef] as any[]) {
        if (ref.current && mapRef.current) { try { mapRef.current.removeLayer(ref.current); } catch {} ref.current = null; }
      }
      [osmTileLayerRef, noLabelsTileLayerRef].forEach((ref) => {
        if (ref.current && mapRef.current) { try { mapRef.current.removeLayer(ref.current); } catch {} ref.current = null; }
      });
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      isInitialized.current = false;
      isSwitchingDataType.current = false;
    };
  }, [cleanupParticleSystem, memoizedDefaultLocation]);

  useEffect(() => {
    if (isInitialized.current || !isMapReady.current || isLoading) return;
    if (isWaveMode(dataTypeRef.current) && !isGeoJSONReady) return;
    if (!isMapValid()) return;
    isInitialized.current = true;
    (async () => {
      await new Promise((r) => setTimeout(r, 200));
      await fetchAllTimestamps();
      await fetchAndRender(true);
    })();
  }, [isLoading, isGeoJSONReady, fetchAllTimestamps, fetchAndRender, isMapValid]);

  useEffect(() => {
    if (!isAnimating || availableTimesRef.current.length <= 1) {
      if (animationChainRef.current) { clearTimeout(animationChainRef.current); animationChainRef.current = null; }
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !isAnimatingRef.current) return;
      const next = (timeIndexRef.current + 1) % availableTimesRef.current.length;
      timeIndexRef.current = next;
      setDisplayTimeIndex(next);
      await fetchAndRender(false);
      if (!cancelled && isAnimatingRef.current)
        animationChainRef.current = setTimeout(tick, 2000);
    };
    animationChainRef.current = setTimeout(tick, 2000);
    return () => {
      cancelled = true;
      if (animationChainRef.current) { clearTimeout(animationChainRef.current); animationChainRef.current = null; }
    };
  }, [isAnimating, fetchAndRender]);

  useEffect(() => {
    const s = windowWidth.current > 1000;
    if (s !== reduxData.toggle) store.dispatch({ type: "TOGGLE_MENU", payload: s });
    setOpen(s);
  }, []);

  useEffect(() => { setOpen(reduxData.toggle); }, [reduxData.toggle]);

  useEffect(() => {
    const inv = () => {
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize({ animate: false });
          const minZoom = getMinZoom(window.innerWidth);
          mapRef.current.setMinZoom(minZoom);
          (mapRef.current as any).getMinZoom = () => minZoom;
          const currentBounds = mapRef.current.options.maxBounds;
          if (currentBounds) mapRef.current.fitBounds(currentBounds as L.LatLngBoundsExpression, { padding: [10, 10], maxZoom: minZoom, animate: false });
        }
      }, 350);
    };
    window.addEventListener("resize", inv);
    const el = document.querySelector(".main");
    const obs = new MutationObserver((m) => {
      if (!mapRef.current) return;
      for (const x of m) if (x.type === "attributes") { inv(); break; }
    });
    if (el) obs.observe(el, { attributes: true, attributeFilter: ["style", "class"] });
    return () => {
      window.removeEventListener("resize", inv);
      obs.disconnect();
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    };
  }, []);

  const maxZoom = isWaveMode(dataTypeRef.current) ? 5 : 8;

  useEffect(() => {
    if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    resizeTimeoutRef.current = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize({ animate: false });
        const minZoom = getMinZoom(window.innerWidth);
        mapRef.current.setMinZoom(minZoom);
        (mapRef.current as any).getMinZoom = () => minZoom;
      }
    }, 320);
    return () => { if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current); };
  }, [open]);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.setZoom(Math.min(mapRef.current.getZoom() + 1, maxZoom));
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.setZoom(Math.max(mapRef.current.getZoom() - 1, 2));
  }, []);

  const switchToType = useCallback(
    async (newType: DataType) => {
      if (newType === dataTypeRef.current || isSwitchingDataType.current) return;
      isSwitchingDataType.current = true;
      setIsSwitchingData(true);
      setIsAnimating(false);

      if (animationChainRef.current) { clearTimeout(animationChainRef.current); animationChainRef.current = null; }
      activeFetchController.current?.abort();
      cleanupParticleSystem();

      dataCache.current.clear();
      lastFetchedKey.current = "";
      timeIndexRef.current = 0;
      isParticleSystemReady.current = false;
      boundsSetOnce.current = false;
      dataTypeRef.current = newType;
      setDataType(newType);
      setCurrentData(null);
      setAvailableTimes([]);
      setDisplayTimeIndex(0);
      setError(null);
      setIsDataLoading(true);

      await new Promise<void>((r) => requestAnimationFrame(() => r()));

      try {
        if (!isMapValid()) return;
        ensureMapPanePosition();

        if (isWaveMode(newType) && (!landGeoJSONRef.current || !boundariesGeoJSONRef.current)) {
          const [l, b] = await Promise.all([loadLandGeoJSON(), loadBoundariesGeoJSON()]);
          if (l) landGeoJSONRef.current = l;
          if (b) boundariesGeoJSONRef.current = b;
        }

        const [times] = await Promise.all([fetchAllTimestamps()]);
        if (times?.length) setAvailableTimes(times);
        await fetchAndRender(true);
      } catch (e: any) {
        setError(`Failed to switch: ${e.message}`);
      } finally {
        setIsDataLoading(false);
        isSwitchingDataType.current = false;
        setIsSwitchingData(false);
      }
    },
    [fetchAllTimestamps, fetchAndRender, cleanupParticleSystem, setIsAnimating, isMapValid, ensureMapPanePosition]
  );

  const handleWindClick = useCallback(() => {
    setTopLevel("wind");
    setActiveSubType(null);
    switchToType("wind");
  }, [switchToType]);

  const handleWaveClick = useCallback(() => {
    setTopLevel("wave");
    setActiveSubType(null);
    switchToType("wave");
  }, [switchToType]);

  const handleSubTypeClick = useCallback(
    (id: DataType) => {
      setTopLevel("wave");
      setActiveSubType(id === "wave" ? null : (id as WaveSubType));
      switchToType(id);
    },
    [switchToType]
  );

  const handleTimeIndexChange = useCallback(
    (i: number) => {
      timeIndexRef.current = i;
      setDisplayTimeIndex(i);
      fetchAndRender(false);
    },
    [fetchAndRender]
  );

  const toggleAnimation = useCallback(() => {
    setIsAnimating(!isAnimatingRef.current);
  }, [setIsAnimating]);

  const activeColorScale = getColorScale(dataType);
  const legendLabel = getLegendLabel(dataType);

  const waveButtonLabel = useMemo(() => {
    if (topLevel === "wave" && activeSubType)
      return WAVE_SUB_TYPES.find((s) => s.id === activeSubType)?.label ?? "Wave";
    return "Wave";
  }, [topLevel, activeSubType]);

  const dropdownItems = useMemo((): { id: DataType; label: string }[] => {
    if (!activeSubType) return WAVE_SUB_TYPES;
    return [{ id: "wave" as DataType, label: "Wave" }, ...WAVE_SUB_TYPES.filter(({ id }) => id !== activeSubType)];
  }, [activeSubType]);

  return (
    <div>
      <div
        style={
          open
            ? {
                marginLeft: mapLeftOffset !== 0 ? drawerWidth + mapLeftOffset : drawerWidth,
                width: `calc(100% - ${drawerWidth + mapLeftOffset}px)`,
              }
            : {}
        }
        className="main"
      >
        <div className="leaflet-map-container">
          {isLoading && (
            <div className="leaflet-map-loading-overlay">
              <div className="loading-content">
                <div className="loading-spinner" />
                <p className="loading-text">{loadingMessage}</p>
              </div>
            </div>
          )}

          <div className="leaflet-map-top-controls">
            {currentData && (
              <div className="top-controls-row">
                <div className="control-group">
                  <button
                    onClick={handleWindClick}
                    className={`control-button ${topLevel === "wind" ? "active wind" : ""}`}
                    disabled={isSwitchingData}
                    style={{ opacity: isSwitchingData ? 0.6 : 1, pointerEvents: isSwitchingData ? "none" : "auto" }}
                  >
                    <span>Wind</span>
                  </button>

                  <div className="wave-dropdown-wrap">
                    <button
                      onClick={handleWaveClick}
                      className={`control-button wave-trigger ${topLevel === "wave" ? "active wave" : ""}`}
                      disabled={isSwitchingData}
                      style={{ opacity: isSwitchingData ? 0.6 : 1, pointerEvents: isSwitchingData ? "none" : "auto" }}
                    >
                      <span className="wave-btn-label">{waveButtonLabel}</span>
                      <span className="wave-arrow">{"\u25B2"}</span>
                    </button>

                    <div className="wave-dropdown-menu">
                      {dropdownItems.map(({ id, label }) => (
                        <div
                          key={id}
                          className="wave-dropdown-item"
                          onClick={() => !isSwitchingData && handleSubTypeClick(id)}
                        >
                          <span className={`wave-dot wave-dot-${id.replace(/_/g, "-")}`} />
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && !isDataLoading && (
              <div className="error-banner">
                <strong>Error:</strong> {error}
                <button
                  onClick={() => {
                    setError(null);
                    isInitialized.current = false;
                    isParticleSystemReady.current = false;
                    (async () => {
                      try {
                        await fetchAllTimestamps();
                        await fetchAndRender(true);
                        isInitialized.current = true;
                      } catch (e: any) {
                        setError(`Retry failed: ${e.message}`);
                      }
                    })();
                  }}
                  style={{ marginLeft: "10px", padding: "4px 8px" }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {currentData && (
            <div className="leaflet-map-zoom-controls">
              <div className="zoom-controls">
                <button onClick={handleZoomIn} className="zoom-button zoom-in" title="Zoom In">+</button>
                <button onClick={handleZoomOut} className="zoom-button zoom-out" title="Zoom Out">-</button>
              </div>
            </div>
          )}

          {currentData && (
            <div
              className="color-legend"
              style={{
                position: "absolute",
                bottom: "100px",
                right: "20px",
                backgroundColor: "rgba(255,255,255,0.15)",
                padding: "6px 10px",
                borderRadius: "6px",
                zIndex: 1000,
                color: "#fff",
                fontSize: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                maxWidth: "90vw",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "5px", fontSize: "10px", textAlign: "center", color: "#fff", textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
                {legendLabel}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", justifyContent: "center", alignItems: "center" }}>
                {activeColorScale.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <div style={{ width: "10px", height: "10px", backgroundColor: item.color, border: "1px solid rgba(0,0,0,0.4)", borderRadius: "2px", flexShrink: 0, boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }} />
                    <span style={{ fontSize: "9px", fontWeight: "500", whiteSpace: "nowrap", color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div ref={mapContainerRef} style={{ height }} className="leaflet-map-viewer" />

          {!currentData && !isLoading && !isDataLoading && isInitialized.current && (
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1000 }}>
              <CircularProgress />
            </div>
          )}

          {currentData && availableTimes.length > 0 && (
            <div
              style={{
                position: "fixed",
                bottom: isMobile ? "-15px" : "-15px",
                left: isMobile ? "5px" : open ? `${drawerWidth + 20}px` : "20px",
                right: isMobile ? "5px" : "20px",
                maxWidth: isMobile ? "calc(100vw - 10px)" : open ? `calc(100vw - ${drawerWidth + 40}px)` : "calc(100vw - 40px)",
                transition: "left 0.3s ease-in-out, max-width 0.3s ease-in-out",
                zIndex: 1001,
                padding: isMobile ? "6px 8px" : "12px 20px",
              }}
              className="leaflet-map-time-controls"
            >
              <TimeControls
                displayTimeIndex={displayTimeIndex}
                availableTimes={availableTimes}
                isDataLoading={isDataLoading}
                isAnimating={isAnimating}
                onTimeIndexChange={handleTimeIndexChange}
                onToggleAnimation={toggleAnimation}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(LeafletMap);